import { isServer } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import type { Entries } from "type-fest";
import * as CookieConsent from "vanilla-cookieconsent";
import "vanilla-cookieconsent/dist/cookieconsent.css";
import "./cookie-consent.css";

import { useDi } from "~/entities/di";
import {
  CATEGORIES,
  Category,
  CATEGORY_BY_SERVICE,
  Service,
  SERVICE_LABEL_TK,
} from "~/shared/cookie";
import { LanguageCode, Tk, useTranslation2 } from "~/shared/i18n";
import { useEvent } from "~/shared/react";

if (!isServer) {
  const windowWithDataLayer = window as typeof window & {
    dataLayer: IArguments[];
  };

  windowWithDataLayer.dataLayer ||= [];
  windowWithDataLayer.gtag ||= function gtag() {
    // eslint-disable-next-line prefer-rest-params
    windowWithDataLayer.dataLayer?.push(arguments);
  };
}

function updateGtagConsent() {
  window.gtag(
    "consent",
    "update",
    Object.fromEntries(
      Object.values(Service).map((service) => [
        service,
        CookieConsent.acceptedService(service, CATEGORY_BY_SERVICE[service]!)
          ? "granted"
          : "denied",
      ]),
    ),
  );
}

function useCookieConsent() {
  const {
    translation: { t, i18n },
    postproc: { tc },
  } = useTranslation2();

  const { eventBus } = useDi();

  const { language } = i18n;

  const visibleModals = useRef(new Set());

  const handler = useEvent(() => {
    eventBus.emit("cookieConsentChanged");
    updateGtagConsent();
  });

  useEffect(() => {
    void CookieConsent.run({
      onFirstConsent: handler,

      onConsent: handler,

      onChange: handler,

      onModalShow: ({ modalName }) => {
        visibleModals.current.add(modalName);
      },

      onModalHide: ({ modalName }) => {
        visibleModals.current.delete(modalName);
      },

      categories: Object.fromEntries(
        (Object.entries(CATEGORIES) as Entries<typeof CATEGORIES>).map(
          ([category, { services = {}, ...rest } = {}]) => [
            category,
            {
              ...rest,
              services: Object.fromEntries(
                (Object.entries(services) as Entries<typeof services>).map(
                  ([name, { [SERVICE_LABEL_TK]: labelTk }]) => [
                    name,
                    {
                      label: t(labelTk),
                      [SERVICE_LABEL_TK]: labelTk,
                    } satisfies CookieConsent.Service,
                  ],
                ),
              ),
            },
          ],
        ),
      ),

      language: {
        default: language,
        translations: Object.fromEntries(
          Object.values(LanguageCode).map((lng) => {
            const tl = (key: Tk) =>
              t(key, {
                lng,
              });
            const tcl = (key: Tk) =>
              tc(key, {
                lng,
              });

            return [
              lng,
              {
                consentModal: {
                  title: tcl(Tk.CONSENT_MODAL_TITLE),
                  description: tl(Tk.CONSENT_MODAL_DESCRIPTION),
                  acceptAllBtn: tcl(Tk.CONSENT_MODAL_ACCEPT_ALL),
                  acceptNecessaryBtn: tcl(Tk.CONSENT_MODAL_ACCEPT_NECESSARY),
                  showPreferencesBtn: tcl(Tk.SETTINGS),
                },
                preferencesModal: {
                  title: tcl(Tk.CONSENT_PREFERENCES_MODAL_TITLE),
                  acceptAllBtn: tcl(Tk.CONSENT_MODAL_ACCEPT_ALL),
                  acceptNecessaryBtn: tcl(Tk.CONSENT_MODAL_ACCEPT_NECESSARY),
                  savePreferencesBtn: tcl(Tk.CONSENT_PREFERENCES_MODAL_SAVE_PREFERENCES),
                  closeIconLabel: tcl(Tk.CLOSE),
                  serviceCounterLabel: tcl(Tk.CONSENT_PREFERENCES_MODAL_SERVICE),
                  sections: [
                    {
                      title: tcl(Tk.CONSENT_PREFERENCES_MODAL_SECTION_NECESSARY_TITLE),
                      description: tl(Tk.CONSENT_PREFERENCES_MODAL_SECTION_NECESSARY_DESCRIPTION),
                      linkedCategory: Category.necessary,
                    },
                    {
                      title: tcl(Tk.CONSENT_PREFERENCES_MODAL_SECTION_ANALYTICS_TITLE),
                      description: tl(Tk.CONSENT_PREFERENCES_MODAL_SECTION_ANALYTICS_DESCRIPTION),
                      linkedCategory: Category.analytics,
                    },
                    {
                      title: tcl(Tk.CONSENT_PREFERENCES_MODAL_SECTION_ADVERTISING_TITLE),
                      description: tl(Tk.CONSENT_PREFERENCES_MODAL_SECTION_ADVERTISING_DESCRIPTION),
                      linkedCategory: Category.advertisement,
                    },
                    {
                      title: tcl(Tk.CONSENT_PREFERENCES_MODAL_SECTION_FUNCTIONALITY_TITLE),
                      description: t(
                        Tk.CONSENT_PREFERENCES_MODAL_SECTION_FUNCTIONALITY_DESCRIPTION,
                      ),
                      linkedCategory: Category.functionality,
                    },
                    {
                      title: tcl(Tk.CONSENT_PREFERENCES_MODAL_SECTION_SECURITY_TITLE),
                      description: tl(Tk.CONSENT_PREFERENCES_MODAL_SECTION_SECURITY_DESCRIPTION),
                      linkedCategory: Category.security,
                    },
                  ],
                },
              },
            ];
          }),
        ),
      },
    });
  }, []);

  useEffect(() => {
    const params = [
      "languageChanged",
      async (lng: string) => {
        for (const { services = {} } of Object.values(CookieConsent.getConfig("categories"))) {
          for (const service of Object.values(services)) {
            service.label = t(service[SERVICE_LABEL_TK]);
          }
        }
        await CookieConsent.setLanguage(lng);
      },
    ] as const;
    i18n.on(...params);

    return () => {
      i18n.off(...params);
    };
  }, [i18n, t, tc]);
}

export { useCookieConsent };
